import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { setRequestLanguage } from '@/api/client'

export type Locale = 'ar' | 'en'
export type ThemePref = 'light' | 'dark' | 'system'
export type Density = 'comfortable' | 'compact'

interface UiState {
  locale: Locale
  theme: ThemePref
  density: Density
  sidebarCollapsed: boolean
  /** The audible alert on every surfaced error. On by default — a silent failure on an unattended screen is what it exists to prevent. */
  errorSound: boolean
  /** Ids of expanded navigation groups — persisted so the sidebar reopens the way the user left it. */
  expandedNav: string[]
  mobileNavOpen: boolean
  /** The page last opened from the phone menu: its back arrow returns to that menu, on that page's section. */
  menuOrigin: string | null
  setMenuOrigin: (path: string | null) => void
  /** The menu is being opened by a page's back arrow: it opens on that page's section. */
  menuBack: boolean
  setMenuBack: (on: boolean) => void
  setLocale: (l: Locale) => void
  setTheme: (t: ThemePref) => void
  setDensity: (d: Density) => void
  setErrorSound: (on: boolean) => void
  toggleSidebar: () => void
  setMobileNav: (open: boolean) => void
  toggleNavGroup: (id: string) => void
  expandNavGroups: (ids: string[]) => void
  /** Opens one top-level section and closes the other top-level ones (sub-groups keep their state). */
  openNavSection: (id: string, topIds: string[]) => void
  /** The pages pinned to the top of the sidebar (owner, 2026-10-20: «المفضلة»), in the order they were starred. */
  favorites: string[]
  toggleFavorite: (pageId: string) => void
}

export const useUi = create<UiState>()(
  persist(
    (set) => ({
      locale: 'ar',
      theme: 'light',
      density: 'comfortable',
      sidebarCollapsed: false,
      errorSound: true,
      expandedNav: [],
      mobileNavOpen: false,
      menuOrigin: null,
      setMenuOrigin: (menuOrigin) => set({ menuOrigin }),
      menuBack: false,
      setMenuBack: (menuBack) => set({ menuBack }),
      setLocale: (locale) => set({ locale }),
      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      setErrorSound: (errorSound) => set({ errorSound }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setMobileNav: (mobileNavOpen) => set({ mobileNavOpen }),
      toggleNavGroup: (id) => set((s) => ({ expandedNav: s.expandedNav.includes(id) ? s.expandedNav.filter((x) => x !== id) : [...s.expandedNav, id] })),
      expandNavGroups: (ids) => set((s) => ({ expandedNav: [...new Set([...s.expandedNav, ...ids])] })),
      openNavSection: (id, topIds) => set((s) => ({ expandedNav: [...new Set([...s.expandedNav.filter((x) => !topIds.includes(x)), id])] })),
      favorites: [],
      toggleFavorite: (pageId) => set((s) => ({ favorites: s.favorites.includes(pageId) ? s.favorites.filter((x) => x !== pageId) : [...s.favorites, pageId] })),
    }),
    {
      name: 'aos-ui',
      // v2: the product default became the light theme; older saved 'system' preferences are reset once.
      version: 2,
      migrate: (persisted) => ({ ...(persisted as object), theme: 'light' }) as UiState,
      partialize: ({ locale, theme, density, sidebarCollapsed, errorSound, expandedNav, favorites }) => ({ locale, theme, density, sidebarCollapsed, errorSound, expandedNav, favorites }),
    },
  ),
)

/** Applies locale / theme to <html> and keeps the API language header in sync. */
export function applyUiEffects() {
  const apply = () => {
    const { locale, theme, density } = useUi.getState()
    const root = document.documentElement
    root.lang = locale
    root.dir = locale === 'ar' ? 'rtl' : 'ltr'
    const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    root.dataset.theme = dark ? 'dark' : 'light'
    root.dataset.density = density
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#0d1320' : '#ffffff')
    setRequestLanguage(locale)
  }
  apply()
  useUi.subscribe(apply)
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', apply)
}
